<template>
  <div ref="root" class="resize-observer" tabindex="-1" />
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeMount, nextTick } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-resize-observer />
   */
  name: 'AkResizeObserver',

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['notify'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all
   * of the attributes for the component to use.
   */
  setup(props, { emit }) {
    const root = ref(null)
    const width = ref(0)
    const height = ref(0)
    const resizeObject = ref(null)

    /**
     * @type {Function}
     * @description Emits the notify event with the width and height values
     */
    const notify = () => {
      emit('notify', {
        width: width.value,
        height: height.value,
      })
    }

    /**
     * @description Compares the width and height of the element against the
     * size of the parent, and notifys when the size changes.
     */
    const compareAndNotify = () => {
      if (
        width.value !== root.value.offsetWidth ||
        height.value !== root.value.offsetHeight
      ) {
        width.value = root.value.offsetWidth
        height.value = root.value.offsetHeight
        notify()
      }
    }

    /**
     * @description Sets up the resize listener on the `_resizeObject`
     * element.
     */
    const addResizeHandlers = () => {
      resizeObject.value.contentDocument.defaultView.addEventListener(
        'resize',
        compareAndNotify
      )
      compareAndNotify()
    }

    /**
     * @description Removes the resize listener from the `_resizeObject`
     * element.
     */
    const removeResizeHandlers = () => {
      if (resizeObject.value && resizeObject.value.onload) {
        if (resizeObject.value.contentDocument) {
          resizeObject.value.contentDocument.defaultView.removeEventListener(
            'resize',
            compareAndNotify
          )
        }
        root.value.removeChild(resizeObject.value)
        resizeObject.value.onload = null
        resizeObject.value = null
      }
    }

    onMounted(() => {
      nextTick(() => {
        width.value = root.value && root.value.offsetWidth
        height.value = root.value && root.value.offsetHeight
      })

      const object = document.createElement('object')
      resizeObject.value = object
      object.setAttribute('aria-hidden', 'true')
      object.setAttribute('tabindex', -1)
      object.onload = addResizeHandlers
      object.type = 'text/html'
      object.data = 'about:blank'
      root.value.appendChild(object)
    })

    onBeforeMount(() => {
      removeResizeHandlers()
    })

    return {
      root,
      width,
      height,
      resizeObject,
    }
  },
})
</script>

<style lang="scss">
.resize-observer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  border: none;
  background-color: transparent;
  pointer-events: none;
  display: block;
  overflow: hidden;
  opacity: 0;
}
.resize-observer :deep(object) {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
}
</style>
